import { BFilter, BFilterOptions } from "./components/BFilterEditor/BFilterEditor.types";
import { FullReport } from "./components/ContactsEditor/schema";
import { UploadType } from "./types";

export function durationAtLeast<T>(promise: PromiseLike<T>, time: number): Promise<T> {
  return new Promise((resolve, reject) => {
    Promise.all([promise, new Promise((resolveTimeout) => setTimeout(resolveTimeout, time))])
      .then(([response]) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

// Date.parse is badly broken in chrome
// Parse date in "yyy-MM-dd" format interpreting
export function parseDate(value: string, isUtc = false): Date | null {
  let reg = /(\d{4})-(\d{1,2})-(\d{1,2})/;
  let result = value.match(reg);
  if (result == null) {
    return null;
  }
  let year = parseInt(result[1]);
  let month = parseInt(result[2]);
  let day = parseInt(result[3]);

  let date: Date;
  if (isUtc) {
    date = new Date(Date.UTC(year, month - 1, day));
  } else {
    date = new Date(year, month - 1, day);
  }
  if (date.getUTCFullYear() != year) {
    return null;
  }
  if (date.getUTCMonth() != month - 1) {
    return null;
  }
  if (date.getUTCDate() != day) {
    return null;
  }
  return date;
}

export function toJsonPatch(value: any, include?: string[]) {
  return (include || Object.getOwnPropertyNames(value)).map((it) => {
    if (value[it] == null) {
      return { op: "remove", path: `/${it}` };
    } else {
      return { op: "add", path: `/${it}`, value: value[it] };
    }
  });
}

export function toJsonPost(value: any, exclude?: string[]) {
  var payload = {} as any;
  for (let key in value) {
    if (!key.startsWith("_") && (!exclude || !exclude.includes(key))) {
      payload[key] = value[key];
    }
  }
  return payload;
}

export function splitOnWhitespace(str: string) {
  return (str || "").split(/\s+/).filter((it) => !isEmptyOrWhitespace(it));
}

export function isEmptyOrWhitespace(str: string) {
  return str == null || str.match(/^\s*$/) != null;
}
export function isNumber(value: string) {
  var re = /^[0-9]*$/;
  return re.test(String(value));
}

export function isEmailAddress(value: string) {
  var re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(value)?.toLowerCase());
}

export function unique<T>(array: T[]): T[] {
  return [...new Set(array.map((it) => JSON.stringify(it)))].map((it) => JSON.parse(it) as T);
}

export function nullToEmpty<T>(obj: T, props: string[]): T {
  props.forEach((it) => {
    if (!(obj as any)[it]) {
      (obj as any)[it] = "";
    }
  });
  return obj;
}

export function addInactivityListener(listener: () => any, inactivityTimeoutSec: number) {
  var time: any;
  function resetTimer() {
    clearTimeout(time);
    time = setTimeout(listener, inactivityTimeoutSec * 1000);
  }
  window.addEventListener("load", resetTimer, true);
  var events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart"];
  events.forEach(function (name) {
    document.addEventListener(name, resetTimer, true);
  });
}

export interface ZipDefinition {
  [key: string]: any[];
}

export function zip<T>(definition: ZipDefinition): T[] {
  const entries = Object.entries(definition);
  const maxLength = Math.max(...entries.map(([k, v]) => v.length));
  const result: T[] = [];
  for (var i = 0; i < maxLength; i++) {
    const obj = Object.fromEntries(entries.map(([k, v]) => [k, v[i]])) as T;
    result.push(obj);
  }
  return result;
}

export function asArray<T>(value: T | T[]): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }
  return [value];
}

export function escapeFilterValue(value: any): string {
  if (typeof value == "string") {
    return `"${value.replace(/"/g, '\\"')}"`;
  }
  return value;
}

export function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function isString(value: any): boolean {
  return typeof value === "string" || value instanceof String;
}

export const ianaTimeZoneNamesUs = [
  "America/Adak",
  "America/Anchorage",
  "America/Boise",
  "America/Chicago",
  "America/Denver",
  "America/Detroit",
  "America/Indiana/Indianapolis",
  "America/Indiana/Knox",
  "America/Indiana/Marengo",
  "America/Indiana/Petersburg",
  "America/Indiana/Tell_City",
  "America/Indiana/Vevay",
  "America/Indiana/Vincennes",
  "America/Indiana/Winamac",
  "America/Juneau",
  "America/Kentucky/Louisville",
  "America/Kentucky/Monticello",
  "America/Los_Angeles",
  "America/Menominee",
  "America/Metlakatla",
  "America/New_York",
  "America/Nome",
  "America/North_Dakota/Beulah",
  "America/North_Dakota/Center",
  "America/North_Dakota/New_Salem",
  "America/Phoenix",
  "America/Sitka",
  "America/Yakutat",
];

export function sleep(ms: number): any {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getFiltersFromString(filters: string): BFilterOptions {
  let filterOptions: BFilterOptions = {
    filters: [],
  };
  let filterArray = filters.split(",");
  filterArray.forEach((filterText) => {
    let item = filterText.split(":");
    let filter: BFilter = {
      property: item[0],
      operation: item[1],
      value: item[2].replace(/'/g, ""),
    };
    filterOptions.filters.push(filter);
  });

  return filterOptions;
}

export function addIdPropertyToTargetArrays(sourceObject) {
  let property: keyof typeof sourceObject;

  for (property in sourceObject) {
    if (Array.isArray(sourceObject[property])) {
      let counter = 0;

      for (let index in sourceObject[property]) {
        if (typeof sourceObject[property][index] === "object") {
          counter++;
          sourceObject[property][index].generated_id = `${property}${counter}`;

          addIdPropertyToTargetArrays(sourceObject[property][index]);
        }
      }
    }
  }
}

export function handleErrorInResponse(error: any) {
  console.error("Error encountered while retrieving data ", error);

  if (error && error.response) {
    let status = error.response.status;
    if (status == 401) {
      window.location.href = `signin.aspx?returnurl=${encodeURI(`~/${window.location.pathname.substring(window.location.pathname.lastIndexOf("/") + 1)}`)}`;
    } else if (status === 500) {
      // I'm letting the 400 range status codes through (except 401) because they're often used on the page to show alert boxes
      window.location.href = `error.aspx?errorcode=${status}`;
    }
  }
}

export function deepCopy<T>(instance : T) : T {
  if ( instance == null){
      return instance;
  }

  // handle Dates
  if (instance instanceof Date) {
      return new Date(instance.getTime()) as any;
  }

  // handle Array types
  if (instance instanceof Array){
      var cloneArr = [] as any[];
      (instance as any[]).forEach((value)  => {cloneArr.push(value)});
      // for nested objects
      return cloneArr.map((value: any) => deepCopy<any>(value)) as any;
  }
  // handle objects
  if (instance instanceof Object) {
      var copyInstance = { ...(instance as { [key: string]: any }
      ) } as { [key: string]: any };
      for (var attr in instance) {
          if ( (instance as Object).hasOwnProperty(attr)) 
              copyInstance[attr] = deepCopy<any>(instance[attr]);
      }
      return copyInstance as T;
  }
  // handling primitive data types
  return instance;
}

export function isText(data: unknown): data is string {
  return typeof data === 'string';
};

export function getSourceNumberFromText(sourceName: string): number {
   return UploadType[sourceName as keyof typeof UploadType];
}
export async function getHtmlContent(url): Promise<string> {
  try {
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.text();

          if(data.indexOf('Sign in')> 0 || data.indexOf('sign-in')> 0) {
              window.location.href = `signin.aspx?returnurl=${encodeURI(`~/${window.location.pathname.substring(window.location.pathname.lastIndexOf("/") + 1)}`)}`;
        }

        return data;
      }
      return `HTTP error! status: ${response.status}`;
  } catch (error) {
      return `HTTP error: There was a problem with the fetch operation. ${error}`;
  }
}